import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, FormGroup, Grid, Paper, Snackbar, Stack, TextField, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router';
import './AdminScene.css';
import { Close } from '@mui/icons-material';
import ChangePasswordDialog from '../Login/ChangePasswordDialog';
import { TenantUserDTO, UsersGateApiClient } from '../Api/ApiServerHooks';
import { useGateMainContext } from '../GateMainContext';
import { useGateApi } from '../Api/useGateApi';
import { useTranslation } from 'react-i18next';
import LanguagePicker from '../Shared/LanguagePicker';

export default function UserScene() {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const { userData, refreshUserData } = useGateMainContext();
    const { authorizedApi } = useGateApi(UsersGateApiClient);
    const [selectedUser, setSelectedUser] = useState<Partial<TenantUserDTO>>({
        isAdmin: false,
        isTenantManager: false,
    });
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [saveConfirm, setSaveConfirm] = useState(false);

    useEffect(() => {
        if (userData?.isAdmin || userData?.id === selectedUser?.id)
            setButtonDisabled(false);

        setSelectedUser(new TenantUserDTO({ id: userData?.id, name: userData?.firstName, surname: userData?.lastName, email: userData?.email, isAdmin: userData?.isAdmin, isTenantManager: userData?.isTenantManager, canCreateTenants: userData?.canCreateTenants, language: userData?.language }));
    }, [userData, selectedUser?.id]);

    function saveUser() {
        if (selectedUser) {
            setButtonDisabled(true);
            authorizedApi?.saveUserData(new TenantUserDTO({ id: selectedUser.id, name: selectedUser.name, surname: selectedUser.surname, isAdmin: selectedUser.isAdmin, canCreateTenants: selectedUser.canCreateTenants, language: selectedUser.language })).then((d) => {
                if (d) {
                    refreshUserData();
                    setSaveConfirm(true);
                }
                setButtonDisabled(false);
            }).catch((e) => {
                setButtonDisabled(false);
            });
        }
    }

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', position: 'fixed', top: 0, width: '100%', backgroundColor: 'background.default', height: '64px', zIndex: 5000, boxShadow: 'rgb(0 0 0 / 20%) 0px 6px 18px', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={6}>
                    <Grid item xs={8}>
                        <Box sx={{ float: 'left', margin: '5px', marginTop: '8px', marginLeft: '30px' }}>
                            <Typography variant='h5' color='primary' sx={{ margin: '8px', fontWeight: 'bolder' }} >{t('Users.AccountManagement')}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box sx={{ float: 'right', margin: '5px', marginTop: '8px', marginRight: '10px', display: 'flex' }}>
                            <Box sx={{
                                width: "140px",
                                height: "40px",
                                marginTop: '8px',
                                marginRight: '20px'
                            }}>
                                <Button onClick={() => { navigate('/portal'); }} style={{
                                    width: "140px",
                                    height: "40px"
                                }} >
                                    <Box className="close-button-div" sx={{
                                        display: 'flex'
                                    }}>
                                        <Typography variant='h1' color='primary' lineHeight={'42px'} fontSize={16}>{t('Users.Close')}</Typography>
                                        <Close color='primary' sx={{ marginTop: '7px' }} />
                                    </Box>
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            {selectedUser && <Box sx={{ pt: '96px', px: 2, pb: 6, display: 'flex', justifyContent: 'center' }}>
                <Paper elevation={2} sx={{ width: '100%', maxWidth: 720, borderRadius: 3, p: { xs: 3, sm: 5 } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                        <Box className="circle-big" sx={{ m: 0 }}>
                            <p className="circle-big-inner" >
                                {selectedUser.name ? selectedUser.name.substring(0, 1) : ''}{selectedUser.surname ? selectedUser.surname.substring(0, 1) : ''}
                            </p>
                        </Box>
                        <Typography variant='h6' sx={{ mt: 1.5, fontWeight: 600 }}>
                            {[selectedUser.name, selectedUser.surname].filter(Boolean).join(' ')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            {selectedUser.email ?? ''}
                        </Typography>
                    </Box>

                    <Stack spacing={2.5}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField fullWidth label={t('Users.Name')}
                                size='small'
                                name='userName'
                                variant='outlined'
                                value={selectedUser.name ?? ''}
                                onChange={(e) => { setSelectedUser({ ...selectedUser, name: e.currentTarget.value }); }} />
                            <TextField fullWidth
                                label={t('Users.LastName')}
                                size='small'
                                variant='outlined'
                                value={selectedUser.surname ?? ''}
                                onChange={(e) => { setSelectedUser({ ...selectedUser, surname: e.currentTarget.value }); }} />
                        </Box>
                        <TextField fullWidth
                            disabled
                            label={t('Users.Email')}
                            size='small'
                            variant='outlined'
                            value={selectedUser.email ?? ''}
                            onChange={(e) => { setSelectedUser({ ...selectedUser, email: e.currentTarget.value }); }} />

                        <Box>
                            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5, ml: 0.5 }}>
                                {t('Users.Language')}
                            </Typography>
                            <LanguagePicker showText notChangeLanguage externalLang={selectedUser.language} onLanguageChange={(lang) => {
                                setSelectedUser({ ...selectedUser, language: lang });
                            }} />
                        </Box>

                        <Divider />

                        <FormGroup>
                            <FormControlLabel disabled={userData?.isAdmin !== true} control={<Checkbox checked={selectedUser.isAdmin} onChange={(e) => { setSelectedUser({ ...selectedUser, isAdmin: e.target.checked }); }} />} label={t('Users.Admin')} />
                            <FormControlLabel disabled control={<Checkbox checked={selectedUser.isTenantManager} />} label={t('Administration.TenantManager')} />
                        </FormGroup>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, pt: 1 }}>
                            <Button variant='contained' disabled={buttonDisabled} onClick={saveUser}>{t('Users.Save')}</Button>
                            <Button variant='outlined' disabled={buttonDisabled} onClick={() => { setChangePasswordOpen(true); }}>{t('Users.ChangePassword')}</Button>
                        </Box>
                    </Stack>
                </Paper>
            </Box>
            }

            <Snackbar open={saveConfirm} autoHideDuration={3000} onClose={() => setSaveConfirm(false)}>
                <Alert onClose={() => setSaveConfirm(false)} severity="success" sx={{ width: '100%' }}>
                    {t('Users.Saved')}
                </Alert>
            </Snackbar>
            <ChangePasswordDialog isOpen={changePasswordOpen} onClose={() => { setChangePasswordOpen(false); }} />
        </Box >
    )
}
